/**
 * TableActionMenuPlugin — a per-cell "▾" button that opens a menu of table edits:
 * insert/delete rows & columns, toggle the header row, and delete the table.
 *
 * Scoped to operations that round-trip through GFM Markdown. Merged cells, cell
 * background, column widths, and column-headers are intentionally omitted — none
 * are expressible in Markdown, so they'd silently fail to persist (same rule that
 * keeps Indent/Outdent out of the toolbar).
 */
import { $getNodeByKey, $getSelection, $isRangeSelection } from 'lexical';
import {
  $deleteTableColumnAtSelection,
  $deleteTableRowAtSelection,
  $getTableCellNodeFromLexicalNode,
  $getTableNodeFromLexicalNodeOrThrow,
  $insertTableColumnAtSelection,
  $insertTableRowAtSelection,
  $isTableCellNode,
  $isTableRowNode,
  $isTableSelection,
  TableCellHeaderStates,
} from '@lexical/table';
import { mergeRegister } from '@lexical/utils';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import classes from './TableActionMenuPlugin.module.scss';

/** Find the TableCellNode key for the current selection, or null. */
function $getSelectedCellKey(): string | null {
  const selection = $getSelection();
  if (!$isRangeSelection(selection) && !$isTableSelection(selection)) return null;
  const anchorNode = selection.anchor.getNode();
  const cell = $getTableCellNodeFromLexicalNode(anchorNode);
  return cell ? cell.getKey() : null;
}

function TableActionMenu({
  editor,
  cellKey,
  onClose,
  buttonRef,
}: {
  editor: ReturnType<typeof useLexicalComposerContext>[0];
  cellKey: string;
  onClose: () => void;
  buttonRef: React.RefObject<HTMLButtonElement>;
}) {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handle = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [onClose, buttonRef]);

  // Position the menu under the trigger button, clamped to the viewport.
  useEffect(() => {
    const menu = menuRef.current;
    const btn = buttonRef.current;
    if (!menu || !btn) return;
    const rect = btn.getBoundingClientRect();
    const left = Math.max(4, Math.min(rect.left, window.innerWidth - menu.offsetWidth - 4));
    let top = rect.bottom + 2;
    if (top + menu.offsetHeight > window.innerHeight - 4) {
      top = Math.max(4, rect.top - menu.offsetHeight - 2);
    }
    menu.style.top = `${top}px`;
    menu.style.left = `${left}px`;
  }, [buttonRef]);

  // Run a table mutation with the selection anchored in the captured cell, so the
  // @lexical/table "AtSelection" helpers operate on the right row/column even
  // though the menu (not a cell) currently has focus.
  const run = useCallback(
    (fn: () => void) => {
      editor.update(() => {
        const cell = $getNodeByKey(cellKey);
        if ($isTableCellNode(cell)) {
          cell.selectEnd();
          fn();
        }
      });
      onClose();
    },
    [editor, cellKey, onClose],
  );

  const toggleHeaderRow = useCallback(() => {
    editor.update(() => {
      const cell = $getNodeByKey(cellKey);
      if (!$isTableCellNode(cell)) return;
      const table = $getTableNodeFromLexicalNodeOrThrow(cell);
      const firstRow = table.getFirstChild();
      if (!$isTableRowNode(firstRow)) return;
      // Toggle the header state of every cell in the FIRST row (GFM only supports
      // a single header row — the row above the `| --- |` divider).
      firstRow.getChildren().forEach((c) => {
        if ($isTableCellNode(c)) c.toggleHeaderStyle(TableCellHeaderStates.ROW);
      });
    });
    onClose();
  }, [editor, cellKey, onClose]);

  const deleteTable = useCallback(() => {
    editor.update(() => {
      const cell = $getNodeByKey(cellKey);
      if (!$isTableCellNode(cell)) return;
      $getTableNodeFromLexicalNodeOrThrow(cell).remove();
    });
    onClose();
  }, [editor, cellKey, onClose]);

  const item = (label: string, onClick: () => void, danger = false) => (
    <button
      className={`${classes.menuItem} ${danger ? classes.menuItemDanger : ''}`}
      onClick={onClick}
    >
      {label}
    </button>
  );

  return createPortal(
    <div ref={menuRef} className={classes.menu} data-testid="lexical-table-action-menu">
      {item('Insert row above', () => run(() => $insertTableRowAtSelection(false)))}
      {item('Insert row below', () => run(() => $insertTableRowAtSelection(true)))}
      <div className={classes.divider} />
      {item('Insert column left', () => run(() => $insertTableColumnAtSelection(false)))}
      {item('Insert column right', () => run(() => $insertTableColumnAtSelection(true)))}
      <div className={classes.divider} />
      {item('Toggle header row', toggleHeaderRow)}
      <div className={classes.divider} />
      {item('Delete row', () => run(() => $deleteTableRowAtSelection()), true)}
      {item('Delete column', () => run(() => $deleteTableColumnAtSelection()), true)}
      {item('Delete table', deleteTable, true)}
    </div>,
    document.body,
  );
}

export function TableActionMenuPlugin({ anchorElem }: { anchorElem: HTMLElement }) {
  const [editor] = useLexicalComposerContext();
  const [cellKey, setCellKey] = useState<string | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const updateButton = useCallback(() => {
    editor.getEditorState().read(() => {
      const key = $getSelectedCellKey();
      if (!key) {
        setCellKey(null);
        setPos(null);
        return;
      }
      const cellElem = editor.getElementByKey(key);
      if (!cellElem) {
        setCellKey(null);
        setPos(null);
        return;
      }
      const cellRect = cellElem.getBoundingClientRect();
      const anchorRect = anchorElem.getBoundingClientRect();
      setCellKey(key);
      setPos({
        top: cellRect.top - anchorRect.top + 3,
        left: cellRect.right - anchorRect.left - 22,
      });
    });
  }, [editor, anchorElem]);

  useEffect(() => {
    const onSelChange = () => updateButton();
    document.addEventListener('selectionchange', onSelChange);
    return () => document.removeEventListener('selectionchange', onSelChange);
  }, [updateButton]);

  useEffect(() => {
    return mergeRegister(editor.registerUpdateListener(() => updateButton()));
  }, [editor, updateButton]);

  // Close the menu if the active cell goes away.
  useEffect(() => {
    if (!cellKey) setMenuOpen(false);
  }, [cellKey]);

  if (!cellKey || !pos) return null;

  return (
    <>
      {createPortal(
        <button
          ref={buttonRef}
          type="button"
          className={classes.cellButton}
          data-testid="lexical-table-cell-menu"
          style={{ top: pos.top, left: pos.left }}
          title="Table options"
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen((v) => !v);
          }}
        >
          ▾
        </button>,
        anchorElem,
      )}
      {menuOpen && (
        <TableActionMenu
          editor={editor}
          cellKey={cellKey}
          buttonRef={buttonRef}
          onClose={() => setMenuOpen(false)}
        />
      )}
    </>
  );
}
